import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  paymentMethodsApi,
  type ApprovePaymentMethodRequest,
  type CreatePaymentMethodRequest,
  type RejectPaymentMethodRequest,
  type UpdatePaymentMethodRequest,
} from "../services/paymentMethodsApi";

const KEY = ["payment-methods"] as const;

const invalidateAll = () => {
  queryClient.invalidateQueries({ queryKey: [...KEY] });
  queryClient.invalidateQueries({ queryKey: [...KEY, "pending"] });
  queryClient.invalidateQueries({ queryKey: [...KEY, "mine"] });
};

export const usePaymentMethods = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [...KEY],
    queryFn: paymentMethodsApi.list,
    enabled: options?.enabled ?? true,
  });

export const usePendingPaymentMethods = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [...KEY, "pending"],
    queryFn: paymentMethodsApi.listPending,
    enabled: options?.enabled ?? true,
  });

export const useMyPaymentMethodSubmissions = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [...KEY, "mine"],
    queryFn: paymentMethodsApi.mySubmissions,
    enabled: options?.enabled ?? true,
  });

export const useProvisionPaymentMethod = () =>
  useMutation({
    mutationFn: (payload: CreatePaymentMethodRequest) =>
      paymentMethodsApi.provision(payload),
    onSuccess: invalidateAll,
  });

export const useUpdatePaymentMethod = () =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdatePaymentMethodRequest;
    }) => paymentMethodsApi.update(id, payload),
    onSuccess: invalidateAll,
  });

export const useApprovePaymentMethod = () =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload?: ApprovePaymentMethodRequest;
    }) => paymentMethodsApi.approve(id, payload ?? {}),
    onSuccess: invalidateAll,
  });

export const useRejectPaymentMethod = () =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: RejectPaymentMethodRequest;
    }) => paymentMethodsApi.reject(id, payload),
    onSuccess: invalidateAll,
  });

export const useDeactivatePaymentMethod = () =>
  useMutation({
    mutationFn: (id: string) => paymentMethodsApi.deactivate(id),
    onSuccess: invalidateAll,
  });
